import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { useSite } from "@/context/SiteContext";

export default function Navbar() {
  const { settings, categories } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const menu = settings.menu || [
    { label: "Inicio", url: "/" },
    { label: "Artículos", url: "/articulos" },
    { label: "Revistas", url: "/revistas" },
    { label: "Categorías", url: "/categorias" },
    { label: "Nosotros", url: "/nosotros" },
    { label: "Contacto", url: "/contacto" },
  ];

  const menuCats = categories.filter((c) => c.in_menu).slice(0, 6);
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-sunset-border py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
          <span className={`font-serif text-2xl md:text-[28px] tracking-tight transition-colors ${solid ? "text-sunset-ink" : "text-white"}`}>
            {settings.site_name || "Sunset Magazine"}
          </span>
          <span className={`kicker mt-1 text-[9px] transition-colors ${solid ? "text-sunset-muted" : "text-white/70"}`}>
            Editorial Digital Premium
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {menu.map((m) => (
            <Link
              key={m.url}
              to={m.url}
              data-testid={`nav-${m.label.toLowerCase()}`}
              className={`kicker text-[11px] transition-colors hover:text-sunset-accent ${
                solid ? "text-sunset-ink" : "text-white"
              }`}
            >
              {m.label}
            </Link>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`lg:hidden ${solid ? "text-sunset-ink" : "text-white"}`}
          aria-label="Menú"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-sunset-border mt-3 animate-fade-up">
          <div className="px-6 py-6 flex flex-col gap-1">
            {menu.map((m) => (
              <Link key={m.url} to={m.url} data-testid={`mobile-nav-${m.label.toLowerCase()}`}
                className="font-serif text-2xl py-2 text-sunset-ink hover:text-sunset-accent transition-colors">
                {m.label}
              </Link>
            ))}
            <div className="mt-4 pt-4 border-t border-sunset-border flex flex-wrap gap-x-4 gap-y-2">
              {menuCats.map((c) => (
                <Link key={c.id} to={`/categoria/${c.slug}`} className="kicker text-sunset-muted hover:text-sunset-accent">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
